import { FormEvent, useCallback, useEffect, useState } from 'react'
import { workflowsAPI } from '../services/api'
import type { Workflow, WorkflowRun } from '../types'
import { apiErrorMessage } from '../utils/apiError'

/** What starts a workflow, in the words a creator would use. */
const TRIGGERS: { id: string; label: string; sends: string }[] = [
  {
    id: 'signal_generated',
    label: 'I lock a call',
    sends: 'the call: coin, direction, entry, stop, target, receipt link',
  },
  {
    id: 'signal_closed',
    label: 'One of my calls closes',
    sends: 'the call and its result: win or loss, exit price, % before fees',
  },
  {
    id: 'content_published',
    label: 'A post of mine goes out',
    sends: 'the post: network, link, text (also when you tap "I posted it")',
  },
]
const triggerLabel = (id: string) => TRIGGERS.find((t) => t.id === id)?.label ?? id.replace(/_/g, ' ')

const hostOf = (url: unknown) => {
  try {
    return new URL(String(url)).hostname
  } catch {
    return 'a webhook'
  }
}

const actionLabel = (w: Workflow) =>
  w.actions
    .map((a) => (a.type === 'execute_webhook' ? `send to ${hostOf(a.config?.webhook_url)}` : a.type.replace(/_/g, ' ')))
    .join(', ')

const Runs = ({ id }: { id: number }) => {
  const [runs, setRuns] = useState<WorkflowRun[] | null>(null)
  useEffect(() => {
    workflowsAPI
      .runs(id)
      .then(setRuns)
      .catch(() => setRuns([]))
  }, [id])
  if (!runs) return null
  if (runs.length === 0) return <p className="text-xs text-gray-500">No runs yet.</p>
  return (
    <ul className="mt-1 space-y-0.5 text-xs" aria-label="Runs">
      {runs.map((r) => (
        <li key={r.id} className={r.status === 'failed' ? 'text-danger-700' : 'text-gray-600'}>
          {new Date(r.started_at).toLocaleString()} · {r.status}
          {r.error_message ? `: ${r.error_message}` : ''}
        </li>
      ))}
    </ul>
  )
}

/**
 * Content → Automation: when a call locks or closes, or a post goes out,
 * send it to a webhook (Make, Zapier, n8n...) to do anything there. Runs in
 * the background; failed sends are retried a few times.
 */
const WorkflowsPanel = () => {
  const [workflows, setWorkflows] = useState<Workflow[] | null>(null)
  const [trigger, setTrigger] = useState(TRIGGERS[0].id)
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)
  const [open, setOpen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    workflowsAPI
      .list()
      .then(setWorkflows)
      .catch((e) => setError(apiErrorMessage(e, "Couldn't load your workflows.")))
  }, [])
  useEffect(load, [load])

  const add = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await workflowsAPI.create({
        name: `When ${triggerLabel(trigger).toLowerCase()}`,
        trigger_type: trigger,
        actions: [{ type: 'execute_webhook', config: { webhook_url: url.trim() } }],
      })
      setUrl('')
      load()
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save it."))
    } finally {
      setSaving(false)
    }
  }

  const run = async (job: () => Promise<void>, failed: string) => {
    setError(null)
    try {
      await job()
      load()
    } catch (err) {
      setError(apiErrorMessage(err, failed))
    }
  }

  const sends = TRIGGERS.find((t) => t.id === trigger)?.sends

  return (
    <section className="rounded-lg bg-white p-4 shadow" aria-label="Workflows">
      <h2 className="text-lg font-semibold text-gray-900">Workflows</h2>
      <p className="mt-1 text-sm text-gray-600">
        Send your calls and posts to Make, Zapier or n8n as they happen, to do anything there: a Discord message, a
        sheet row, an email.
      </p>

      <form onSubmit={add} className="mt-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
          <label>
            When
            <select
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              className="ml-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              {TRIGGERS.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-1 items-center">
            send it to
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://hook.make.com/…"
              aria-label="Webhook URL"
              className="ml-2 min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </label>
          <button
            type="submit"
            disabled={saving || !url.trim()}
            className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
        {sends && <p className="text-xs text-gray-500">It sends {sends}.</p>}
      </form>

      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-700">
          {error}
        </p>
      )}

      {workflows && workflows.length > 0 && (
        <ul className="mt-4 divide-y divide-gray-100 text-sm" aria-label="Your workflows">
          {workflows.map((w) => (
            <li key={w.id} className="py-2" data-testid="workflow">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium text-gray-900">{triggerLabel(w.trigger_type)}</span>
                <span className="text-gray-600">→ {actionLabel(w)}</span>
                <span className="text-xs text-gray-500">
                  {w.execution_count} runs{w.failure_count > 0 ? `, ${w.failure_count} failed` : ''}
                </span>
                <span className="ml-auto flex items-center gap-3 text-xs">
                  <label className="flex items-center gap-1 text-gray-700">
                    <input
                      type="checkbox"
                      checked={w.is_active}
                      onChange={() => run(() => workflowsAPI.setActive(w.id, !w.is_active), "Couldn't change it.")}
                    />
                    On
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpen(open === w.id ? null : w.id)}
                    className="text-primary-600 hover:underline"
                  >
                    {open === w.id ? 'Hide runs' : 'Runs'}
                  </button>
                  <button
                    type="button"
                    onClick={() => run(() => workflowsAPI.remove(w.id), "Couldn't delete it.")}
                    className="text-gray-500 hover:underline"
                    aria-label={`Delete ${w.name}`}
                  >
                    Delete
                  </button>
                </span>
              </div>
              {open === w.id && <Runs id={w.id} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default WorkflowsPanel
